import { CircularProgress, InputLabel } from '@material-ui/core';
import { Select, SelectArrowDownIcon, TextInput, Typography } from '@room-match/shared-ui-components';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { COLORS } from 'shared/constants/COLORS';
import { RepresentationType } from 'shared/enums/RepresentationType';
import { IProfileCreatePayload } from 'shared/interfaces/IProfile';
import { profileService } from 'shared/services/profileService';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import { errorResponseToArray } from 'shared/utils/room-shared';
import { SubmitButton } from '../Guardianship.styles';
import GroupPhoto from './GroupPhoto';
import { MainCont } from './styles';

interface Props {
  close: () => void;
}

const { updateTalent, createGuardian, createTalent, getMe, getTalentMembers } = talentService();
const { createProfile, setProfilePrimaryImage, getProfileMatch, createMultipleProfiles, setSelectProfileMedia } =
  profileService();
const { uploadMedia } = uploadService();

const AddFamilyProfile: React.FC<Props> = ({ close }) => {
  const { mutate: _, mutateAsync: bindAsync } = setSelectProfileMedia();
  const { mutateAsync: mutateAsyncUploadMedia, isLoading: isLoadingUploadMedia } = uploadMedia();
  const { mutateAsync: mutateAsyncSetPrimaryImage, isLoading: isSetPrimaryImageLoading } = setProfilePrimaryImage();
  const { mutateAsync: mutateAsyncCreateProfile, isLoading: isLoadingCreateProfile } = createProfile();
  const queryClient = useQueryClient();

  const [familyName, setfamilyName] = useState('');
  const [touched, setTouched] = useState(false);
  const [creating, setCreatng] = useState(false);
  const [familyType, setfamilyType] = useState<'Family' | 'Group' | 'Band' | 'Club' | 'Choir' | 'Other'>('Family');
  const [uploadedImg, setUploadedImg] = useState<{
    name: string;
    type: string;
    size: string;
    file: Blob | undefined;
    imageAttr: { width: number; height: number };
  } | null>(null);

  const nonRepresentedValues: IProfileCreatePayload = {
    representation_type: RepresentationType.FREELANCE,
    country: 'United States',
    note: '',
    agency_id: '',
    confirmed_agreement: true,
    profile_type: 'Actor',
  };

  const handleMakePrimary = (profileId: string, mediaId: string) => {
    const formData = new FormData();
    formData.set('medium_id', mediaId);

    mutateAsyncSetPrimaryImage(
      { profileId, formData },
      {
        onSuccess: () => {
          setCreatng(false);
          queryClient.removeQueries('talent/profiles');
          close();
        },
        onError: () => {
          setCreatng(false);
        },
      },
    );
  };

  const bindToProfile = async (profileId: string, medium_id: string) => {
    await bindAsync(
      { profileId, payload: { id: medium_id, sort: 0, tag_list: 'image' } },
      {
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
    handleMakePrimary(profileId, medium_id);
  };

  const handleSubmit = async () => {
    setCreatng(true);
    if (uploadedImg) {
      if (uploadedImg.file) {
        const formData = new FormData();
        formData.append('attachment', uploadedImg.file);
        formData.append('file_name', uploadedImg.name);
        formData.append('file_width', uploadedImg.imageAttr.width.toString());
        formData.append('file_height', uploadedImg.imageAttr.height.toString());
        formData.append('file_size', uploadedImg.size);
        formData.append('file_type', uploadedImg.type);
        formData.append('media_bin', 'true');

        const uploadedPhoto = await mutateAsyncUploadMedia({ formData });

        if (uploadedPhoto) {
          mutateAsyncCreateProfile(
            { ...nonRepresentedValues, is_group_profile: true, group_category: familyType, group_name: familyName },
            {
              onSuccess: (values) => {
                bindToProfile(values.data.id, uploadedPhoto.data.id);
              },
            },
          );
        }
      }
    }
  };

  const handleUploadImg = async (
    name: string,
    type: string,
    size: string,
    file?: Blob,
    imageAttr?: { width: number; height: number },
  ) => {
    if (file && imageAttr) {
      setUploadedImg({ name, type, size, file, imageAttr });
    }
  };

  return (
    <MainCont>
      <Typography fontSize={24} style={{ alignSelf: 'center', marginBottom: 40 }}>
        Create Family Profile
      </Typography>
      <InputLabel id="representationStatus">Family / Group Name</InputLabel>
      <TextInput
        fullWidth
        errorMessage={touched && familyName === '' ? 'This field is required' : ''}
        name="first_name"
        onChange={(e: any) => setfamilyName(e.target.value)}
        onBlur={() => setTouched(true)}
        // label={'Family / Group Name'}
        placeholder="Enter Group name…"
        value={familyName}
        style={{ marginBottom: 20 }}
      />
      <InputLabel id="representationStatus">Type</InputLabel>
      <Select
        variant="outlined"
        fullWidth
        data={[
          { key: 'Family', value: 'Family' },
          { key: 'Group', value: 'Group' },
          { key: 'Band', value: 'Band' },
          { key: 'Club', value: 'Club' },
          { key: 'Choir', value: 'Choir' },
          { key: 'Other', value: 'Other' },
        ]}
        value={familyType}
        IconComponent={SelectArrowDownIcon}
        name="sag_status"
        onChange={(e: any) => setfamilyType(e.target.value)}
      />
      <GroupPhoto onFileSelected={handleUploadImg} />
      <SubmitButton
        disabled={familyName === '' || creating || !uploadedImg}
        style={{ backgroundColor: COLORS.PRIMARY, width: '100%', height: 53 }}
        onClick={handleSubmit}
      >
        {creating ? (
          <CircularProgress color="inherit" style={{ width: 25, height: 25, marginRight: 10 }} />
        ) : (
          'Create Family Profile'
        )}
      </SubmitButton>
    </MainCont>
  );
};

export default AddFamilyProfile;
